import forEach from 'lodash/forEach';
import trim from 'lodash/trim';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import without from 'lodash/without';
import noop from 'lodash/noop';
import Mark from '@patsnap/mark.js';
import { getKeywordsSplitRegExp, quotationMarkAliens, quotationMarkRegExp } from './contants';
function markAsPromise(instance, keyword, opts = {}) {
    return new Promise((resolve) => {
        const markType = keyword === undefined ? 'unmark' : 'mark';
        if (markType === 'unmark') {
            instance[markType]({ done: resolve });
        }
        else {
            instance[markType](keyword, { done: resolve, ...opts });
        }
    });
}
const excludeEles = ['mark', 'math *', 'maths *', 'svg', 'img'];
let count = 0;
let config = [];
export const defaultMarkOpt = {
    accuracy: 'partially',
    acrossElements: true,
    separateWordSearch: false,
    ignoreJoiners: true,
    exclude: excludeEles,
    done: noop,
};
export const exactMarkOpt = {
    accuracy: 'exactly-in-word',
    acrossElements: true,
    separateWordSearch: false,
    ignoreJoiners: false,
    wildcards: 'enabled',
    diacritics: false,
    exclude: excludeEles,
    done: noop,
};
const listeners = {};
const parser = new DOMParser();
/**
 * 高亮文本
 * @param {String} html
 * @param {Object} options
 */
const markText = (html, options = {}) => {
    const c = config.filter((conf) => conf.keyword);
    if (c.length <= 0)
        return html;
    const parsedHtml = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const dom = parsedHtml.body.children[0];
    const instance = new Mark(dom);
    c.forEach(({ keyword, keywordOnAttr, needExactMatch } /* , index */) => {
        const each = (element) => {
            element.setAttribute('data-keyword', encodeURI(keywordOnAttr));
        };
        const opts = { ...(needExactMatch ? exactMarkOpt : defaultMarkOpt), each };
        instance.mark(keyword, { ...opts, ...options });
    });
    const result = dom.innerHTML;
    return result;
};
/**
 * 高亮 DOM
 * @param {Element} dom
 * @param {Object} options
 */
const mark = async (dom, options = {}) => {
    if (!dom)
        return;
    const c = config.filter((conf) => conf.keyword);
    if (c.length <= 0)
        return;
    const instance = new Mark(dom);
    await markAsPromise(instance);
    instance.unmark({
        done() {
            c.forEach(({ keyword, keywordOnAttr, needExactMatch } /* , index */) => {
                const each = (element) => {
                    element.setAttribute('data-keyword', encodeURI(keywordOnAttr));
                };
                const opts = {
                    ...options,
                    ...(needExactMatch ? exactMarkOpt : defaultMarkOpt),
                    each,
                };
                instance.mark(keyword, opts);
            });
        },
    });
};
/**
 * 高亮每个词解析
 */
function postFormatKeywordsConfigs(configArr) {
    function isStrSurroundByQuotationPair(str) {
        return str.length >= 3
            && quotationMarkAliens.has(str[0]) && quotationMarkAliens.has(str[str.length - 1]);
    }
    return configArr.reduce((array, { keyword, color }) => {
        const quotaArr = keyword.match(quotationMarkRegExp);
        const keywordArr = keyword.replace(quotationMarkRegExp, ' or ').split(getKeywordsSplitRegExp());
        let allKeywordArr = [];
        if (quotaArr && quotaArr.length) {
            allKeywordArr = [...quotaArr];
        }
        if (keywordArr && keywordArr.length) {
            allKeywordArr = [...allKeywordArr, ...keywordArr];
        }
        allKeywordArr.map(str => str.trim())
            .filter((str) => !!str)
            .forEach((str) => {
            const isSurroundByQuo = isStrSurroundByQuotationPair(str);
            array.push({
                keyword: isSurroundByQuo ? trim(str, Array.from(quotationMarkAliens).join('')) : str,
                keywordOnAttr: keyword,
                color,
                needExactMatch: isSurroundByQuo,
            });
        });
        return array;
    }, []);
}
/**
 * 发布高亮配置更新
 * @param {Array<Object>} data
 */
const pub = debounce((value) => {
    const data = postFormatKeywordsConfigs(value);
    // 未发生修改时不推送
    if (isEqual(data, config)) {
        return;
    }
    config = data;
    forEach(listeners, (listener) => {
        listener(config);
    });
}, 500);
const pubWithoutDebounce = (value) => {
    const data = postFormatKeywordsConfigs(value);
    // 未发生修改时不推送
    if (isEqual(data, config)) {
        return;
    }
    config = data;
    forEach(listeners, (listener) => {
        listener(config);
    });
};
/**
 * 隐形高亮，一般用于定位文本位置(不高亮)
 * @param {HTMLElement} dom
 * @param {String} str
 * @param {String} className
 * @returns {Promise<Mark>} Mark的实例
 */
async function invisibleMark(dom, str, className) {
    const instance = new Mark(dom);
    if (str) {
        await markAsPromise(instance, str, {
            element: 'span',
            accuracy: 'partially',
            acrossElements: true,
            separateWordSearch: false,
            ignoreJoiners: true,
            className,
            exclude: without(excludeEles, 'mark'),
        });
    }
    return instance;
}
/**
 * 订阅高亮配置更新
 * @param {Function} listener
 */
const sub = (listener) => {
    count += 1;
    const index = `${count}`;
    listeners[index] = listener;
    if (config.length > 0) {
        listener(config);
    }
    return () => {
        delete listeners[index];
    };
};
/**
 * 获取高亮配置(的拷贝)
 * @return {Array<Object>} config
 */
const get = () => JSON.parse(JSON.stringify(config));
export { postFormatKeywordsConfigs, markText, pub, sub, get, mark, pubWithoutDebounce, invisibleMark, };
//# sourceMappingURL=core.js.map